// Draw a 32x32 "Genesis Degens" PFP from seed + traits. Pure: returns an RGBA buffer.
// Layers: background, skin, eyes, mouth, headwear, accessory (spec section 5, step 3).

import { Rng } from "@degen/sim/rng";
import { shade } from "../avatar/sprite";
import type { PfpTraits } from "@degen/sim/pfp/traits";

export const PFP_SIZE = 32;

export const PFP_SKIN: Record<string, string> = {
  porcelain: "#ffdbb4", sand: "#f1c27d", honey: "#e0ac69", caramel: "#c68642",
  cocoa: "#8d5524", espresso: "#5c3a21", zombie: "#9bc472", gold: "#e8c547",
};

const BG: Record<string, string | [string, string]> = {
  sky: "#6ec3f4", bubblegum: "#f7a9c4", mint: "#b8e986", banana: "#ffd166", lavender: "#c3aed6",
  seafoam: "#9be3de", coral: "#f4978e", concrete: "#a7adba",
  sunset: ["#ff6a5f", "#ffd27b"], matrix: ["#06140c", "#1f6b3a"],
};

const SHIRTS = ["#ef4444", "#3b82f6", "#22c55e", "#a855f7", "#f97316", "#27272a", "#f4f4f5", "#14b8a6", "#ec4899"];
const HAIR = ["#1c1a1f", "#3b2618", "#6b4226", "#d9b46a", "#b5532c", "#ff6fae", "#4fa3ff"];
const HATS = ["#ef4444", "#3b82f6", "#22c55e", "#facc15", "#a855f7", "#27272a", "#f97316", "#f4f4f5"];
const OUTLINE = "#16121d";
const GOLD = "#f5c518";

/** Non-trait colors (shirt, hair, hat) derived from the seed; they don't affect rarity. */
export function pfpColors(seed: number) {
  const rng = Rng.fromSeed(seed ^ 0x0c01_0125);
  return { shirt: rng.pick(SHIRTS), hair: rng.pick(HAIR), hat: rng.pick(HATS) };
}

class Layer {
  px: (string | null)[] = new Array(PFP_SIZE * PFP_SIZE).fill(null);
  set(x: number, y: number, c: string) {
    if (x >= 0 && y >= 0 && x < PFP_SIZE && y < PFP_SIZE) this.px[y * PFP_SIZE + x] = c;
  }
  rect(x0: number, y0: number, x1: number, y1: number, c: string) {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) this.set(x, y, c);
  }
  clear(x: number, y: number) {
    if (x >= 0 && y >= 0 && x < PFP_SIZE && y < PFP_SIZE) this.px[y * PFP_SIZE + x] = null;
  }
  outline() {
    const src = this.px.slice();
    const at = (x: number, y: number) => x >= 0 && y >= 0 && x < PFP_SIZE && y < PFP_SIZE && !!src[y * PFP_SIZE + x];
    for (let y = 0; y < PFP_SIZE; y++)
      for (let x = 0; x < PFP_SIZE; x++)
        if (!src[y * PFP_SIZE + x] && (at(x - 1, y) || at(x + 1, y) || at(x, y - 1) || at(x, y + 1))) this.px[y * PFP_SIZE + x] = OUTLINE;
  }
}

const FALLBACK: PfpTraits = { background: "concrete", skin: "sand", eyes: "normal", mouth: "smile", headwear: "none", accessory: "none" };

export function renderPfp(seed: number, traits: Partial<PfpTraits>): Uint8ClampedArray {
  // Unknown or missing traits (old saves, bad imports) fall back instead of crashing.
  const t = { ...FALLBACK } as PfpTraits;
  for (const k in FALLBACK) {
    const v = (traits as Record<string, string>)[k];
    if (v) (t as Record<string, string>)[k] = v;
  }
  if (!PFP_SKIN[t.skin]) t.skin = FALLBACK.skin;
  if (!BG[t.background]) t.background = FALLBACK.background;
  const colors = pfpColors(seed);
  const skin = PFP_SKIN[t.skin];
  const L = new Layer(); // character layer (gets outlined)
  const fx = new Layer(); // effects drawn over everything without outline (lasers, smoke, bubble)

  // --- hood back (behind head) ---
  const hood = t.headwear === "hoodie";
  if (hood) {
    L.rect(6, 4, 25, 31, colors.hat);
    L.rect(7, 3, 24, 3, colors.hat);
    L.rect(24, 5, 25, 24, shade(colors.hat, 0.8));
  }

  // --- shoulders + neck ---
  const shirt = hood ? colors.hat : colors.shirt;
  L.rect(5, 26, 26, 31, shirt);
  L.rect(4, 28, 27, 31, shirt);
  L.rect(23, 26, 27, 31, shade(shirt, 0.82));
  L.rect(13, 21, 18, 26, shade(skin, 0.85));
  if (hood) {
    L.rect(12, 27, 12, 30, "#f4f4f5");
    L.rect(19, 27, 19, 30, "#f4f4f5");
  } else L.rect(12, 26, 19, 26, shade(shirt, 0.7)); // collar

  // --- head ---
  L.rect(9, 7, 22, 22, skin);
  L.rect(22, 8, 22, 21, shade(skin, 0.88));
  for (const [x, y] of [[9, 7], [22, 7], [9, 22], [22, 22]]) L.clear(x, y);
  L.rect(8, 13, 8, 16, skin); // ears
  L.rect(23, 13, 23, 16, shade(skin, 0.85));

  // --- hair (only when the head is uncovered) ---
  if (t.headwear === "none") {
    L.rect(8, 4, 23, 8, colors.hair);
    L.rect(8, 9, 9, 12, colors.hair);
    L.rect(22, 9, 23, 12, colors.hair);
    L.rect(10, 9, 13, 9, colors.hair);
    L.rect(11, 5, 15, 5, shade(colors.hair, 1.3));
    L.clear(8, 4);
    L.clear(23, 4);
  }

  // --- eyes ---
  const eye = (x: number) => {
    L.rect(x, 13, x + 1, 14, "#ffffff");
    L.set(x + 1, 14, "#1b1b2a");
    L.set(x + 1, 13, "#1b1b2a");
  };
  switch (t.eyes) {
    case "normal":
      eye(12);
      eye(18);
      break;
    case "sleepy":
      L.rect(12, 13, 13, 13, shade(skin, 0.6));
      L.rect(18, 13, 19, 13, shade(skin, 0.6));
      L.rect(12, 14, 13, 14, "#1b1b2a");
      L.rect(18, 14, 19, 14, "#1b1b2a");
      break;
    case "wink":
      eye(12);
      L.rect(18, 14, 20, 14, "#1b1b2a");
      break;
    case "glasses3d":
      L.rect(10, 12, 21, 12, "#f4f4f5");
      L.rect(10, 13, 14, 15, "#ef4444");
      L.rect(17, 13, 21, 15, "#3b82f6");
      L.rect(15, 13, 16, 13, "#f4f4f5");
      L.rect(8, 13, 9, 13, "#f4f4f5");
      L.rect(22, 13, 23, 13, "#f4f4f5");
      break;
    case "shades":
      L.rect(10, 12, 21, 14, "#0f0f14");
      L.rect(8, 12, 9, 12, "#0f0f14");
      L.rect(22, 12, 23, 12, "#0f0f14");
      L.set(11, 12, "#5b6b8c");
      L.set(18, 12, "#5b6b8c");
      L.clear(15, 14);
      L.clear(16, 14);
      L.set(15, 14, skin);
      L.set(16, 14, skin);
      break;
    case "laser":
      L.rect(12, 13, 13, 14, "#ff2a2a");
      L.rect(18, 13, 19, 14, "#ff2a2a");
      // Beams fly off both sides of the frame.
      fx.rect(0, 13, 11, 14, "#ff3b3b");
      fx.rect(20, 13, 31, 14, "#ff3b3b");
      fx.rect(0, 13, 11, 13, "#ffb3b3");
      fx.rect(20, 13, 31, 13, "#ffb3b3");
      fx.set(12, 13, "#ffffff");
      fx.set(19, 13, "#ffffff");
      break;
  }

  // --- mouth ---
  const lip = shade(skin, 0.55);
  switch (t.mouth) {
    case "smile":
      L.rect(14, 19, 17, 19, lip);
      L.set(13, 18, lip);
      L.set(18, 18, lip);
      break;
    case "smirk":
      L.rect(15, 19, 17, 19, lip);
      L.set(18, 18, lip);
      break;
    case "frown":
      L.rect(14, 18, 17, 18, lip);
      L.set(13, 19, lip);
      L.set(18, 19, lip);
      break;
    case "cigar":
      L.rect(14, 19, 16, 19, lip);
      L.rect(17, 18, 23, 19, "#7a4a24");
      L.rect(17, 18, 23, 18, "#94602f");
      fx.set(24, 18, "#ff8c1a");
      fx.set(24, 19, "#ffd23f");
      for (const [x, y] of [[25, 16], [24, 14], [25, 12], [26, 10]]) fx.set(x, y, "#d4d4d8");
      break;
    case "gm": {
      L.rect(15, 18, 16, 19, "#5a1f1f");
      // Speech bubble with "gm"
      fx.rect(22, 1, 31, 8, "#ffffff");
      fx.rect(21, 2, 21, 7, "#ffffff");
      fx.set(21, 9, "#ffffff");
      fx.set(20, 10, "#ffffff");
      const ink = "#111111";
      // g
      fx.rect(23, 3, 25, 3, ink); fx.set(23, 4, ink); fx.set(25, 4, ink); fx.rect(23, 5, 25, 5, ink); fx.set(25, 6, ink); fx.rect(23, 7, 25, 7, ink);
      // m
      fx.rect(26, 4, 30, 4, ink); fx.rect(26, 5, 26, 6, ink); fx.rect(28, 5, 28, 6, ink); fx.rect(30, 5, 30, 6, ink);
      break;
    }
  }

  // --- accessory ---
  switch (t.accessory) {
    case "chain":
    case "diamond": {
      const c = t.accessory === "diamond" ? "#c6f6ff" : GOLD;
      for (const [x, y] of [[10, 27], [11, 28], [12, 29], [13, 29], [14, 30], [17, 30], [18, 29], [19, 29], [20, 28], [21, 27]]) L.set(x, y, c);
      L.rect(15, 30, 16, 31, t.accessory === "diamond" ? "#ffffff" : GOLD);
      if (t.accessory === "diamond") fx.set(16, 30, "#e0fbff");
      break;
    }
    case "earring":
      L.set(8, 17, GOLD);
      L.set(8, 18, GOLD);
      break;
    case "mask":
      L.rect(10, 17, 21, 21, "#e5e7eb");
      L.rect(10, 18, 21, 18, "#cbd5e1");
      L.rect(8, 15, 9, 16, "#e5e7eb");
      L.rect(22, 15, 23, 16, "#e5e7eb");
      break;
    case "headset":
      L.rect(9, 5, 22, 5, "#2a2a33");
      L.rect(6, 12, 8, 17, "#2a2a33");
      L.rect(23, 12, 25, 17, "#2a2a33");
      L.rect(9, 18, 12, 18, "#2a2a33");
      L.set(7, 14, "#22d3ee");
      break;
  }

  // --- headwear ---
  const hat = colors.hat;
  switch (t.headwear) {
    case "cap":
      L.rect(8, 4, 23, 9, hat);
      L.rect(9, 3, 22, 3, hat);
      L.rect(6, 10, 18, 10, shade(hat, 0.75));
      L.rect(14, 5, 17, 6, "#f4f4f5");
      break;
    case "backcap":
      L.rect(8, 4, 23, 9, hat);
      L.rect(9, 3, 22, 3, hat);
      L.rect(14, 8, 17, 9, shade(hat, 0.6));
      L.rect(22, 9, 26, 10, shade(hat, 0.75));
      break;
    case "beanie":
      L.rect(8, 3, 23, 10, hat);
      L.rect(8, 9, 23, 10, shade(hat, 0.78));
      for (let x = 9; x <= 22; x += 2) L.rect(x, 4, x, 8, shade(hat, 0.9));
      L.rect(14, 1, 17, 2, "#f4f4f5");
      break;
    case "bucket":
      L.rect(10, 3, 21, 8, hat);
      L.rect(5, 9, 26, 10, shade(hat, 0.8));
      L.rect(10, 7, 21, 7, shade(hat, 0.7));
      break;
    case "hoodie":
      L.rect(7, 4, 24, 6, colors.hat);
      L.rect(7, 4, 8, 24, colors.hat);
      L.rect(23, 4, 24, 24, shade(colors.hat, 0.85));
      L.rect(9, 7, 22, 7, shade(colors.hat, 1.15));
      break;
    case "crown":
      L.rect(9, 4, 22, 8, GOLD);
      for (const x of [9, 10, 15, 16, 21, 22]) L.rect(x, 1, x, 3, GOLD);
      L.rect(12, 3, 13, 3, GOLD);
      L.rect(18, 3, 19, 3, GOLD);
      L.rect(9, 8, 22, 8, shade(GOLD, 0.75));
      L.rect(15, 5, 16, 6, "#dc2626");
      L.set(11, 6, "#3b82f6");
      L.set(20, 6, "#22c55e");
      break;
  }

  L.outline();

  // --- composite: background, outlined character, effects ---
  const out = new Uint8ClampedArray(PFP_SIZE * PFP_SIZE * 4);
  const bg = BG[t.background];
  for (let y = 0; y < PFP_SIZE; y++) {
    const bgc = typeof bg === "string" ? bg : mix(bg[0], bg[1], y / (PFP_SIZE - 1));
    for (let x = 0; x < PFP_SIZE; x++) {
      let c = fx.px[y * PFP_SIZE + x] ?? L.px[y * PFP_SIZE + x] ?? bgc;
      if (t.background === "matrix" && !fx.px[y * PFP_SIZE + x] && !L.px[y * PFP_SIZE + x] && (x * 7 + y * 3) % 11 === 0) c = "#3ddc84";
      const n = parseInt(c.slice(1), 16);
      const i = (y * PFP_SIZE + x) * 4;
      out[i] = (n >> 16) & 255;
      out[i + 1] = (n >> 8) & 255;
      out[i + 2] = n & 255;
      out[i + 3] = 255;
    }
  }
  return out;
}

function mix(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (s: number) => Math.round(((pa >> s) & 255) * (1 - t) + ((pb >> s) & 255) * t);
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, "0")}`;
}
